import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MoneyInput } from '../MoneyInput';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { FileText, ArrowLeftRight, Receipt, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);
const fmtCurrency = (amount, currency = 'ARS') =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
const fmtDateAr = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

export function AdjustmentDialog({ open, onOpenChange, form, setForm, onSave, onCancel, cashBanks, incomeConcepts, expenseConcepts, submitting = false }) {
  const [suppliers, setSuppliers] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const targetLocked = !!form.target_locked;  // cuando llega precargado desde una cta.cte.

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingList(true);
      try {
        if (form.target_type === 'supplier' && suppliers.length === 0) {
          const r = await axios.get(`${BACKEND_URL}/api/suppliers?is_active=true&limit=5000`, { withCredentials: true });
          if (!cancelled) setSuppliers(r.data?.items || r.data || []);
        } else if (form.target_type === 'resident' && residents.length === 0) {
          const r = await axios.get(`${BACKEND_URL}/api/residents?limit=5000`, { withCredentials: true });
          if (!cancelled) setResidents(r.data?.items || r.data || []);
        }
      } finally { if (!cancelled) setLoadingList(false); }
    })();
    return () => { cancelled = true; };
  }, [open, form.target_type, suppliers.length, residents.length]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-500" />
            Nuevo Ajuste de Tesorería
          </DialogTitle>
          <DialogDescription className="text-xs">
            La <strong>fecha del ajuste</strong> es la que se usa en el Libro Diario, la caja/banco y (si corresponde) la cta.cte. La <strong>fecha de carga</strong> queda registrada aparte para auditoría.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha del ajuste *</Label>
              <Input type="date" max={today()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="adj-date" />
            </div>
            <div>
              <Label>Cargando hoy</Label>
              <Input value={fmtDateAr(today())} disabled readOnly className="bg-muted/40" data-testid="adj-input-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipo de Ajuste *</Label>
              <select value={form.adjustment_type} onChange={(e) => setForm({ ...form, adjustment_type: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="adj-type">
                <option value="debit">DÉBITO (Entrada +)</option>
                <option value="credit">CRÉDITO (Salida -)</option>
              </select>
            </div>
            <div>
              <Label>Caja/Banco *</Label>
              <select value={form.cash_bank_id} onChange={(e) => setForm({ ...form, cash_bank_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="adj-cashbank">
                <option value="">Seleccionar...</option>
                {cashBanks.map(cb => <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name} ({cb.currency})</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipo de Concepto *</Label>
              <select value={form.concept_type} onChange={(e) => setForm({ ...form, concept_type: e.target.value, concept_id: '' })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="adj-concept-type">
                <option value="income">Concepto de Ingreso</option>
                <option value="expense">Concepto de Gasto</option>
              </select>
            </div>
            <div>
              <Label>Concepto *</Label>
              <select value={form.concept_id} onChange={(e) => setForm({ ...form, concept_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="adj-concept">
                <option value="">Seleccionar...</option>
                {form.concept_type === 'income'
                  ? incomeConcepts.map(c => <option key={c.concept_id} value={c.concept_id}>{c.name}</option>)
                  : expenseConcepts.map(c => <option key={c.concept_id} value={c.concept_id}>{c.name}</option>)
                }
              </select>
            </div>
          </div>

          {/* v3.24.0 — Impacto opcional en cta.cte. */}
          <div className="rounded-md border border-dashed p-3 space-y-2 bg-muted/30">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide">Impacta cta.cte. de</Label>
              {targetLocked && <span className="text-[10px] text-muted-foreground">Precargado desde la cta.cte.</span>}
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              {['none', 'supplier', 'resident'].map(t => (
                <label key={t} className={`flex items-center gap-2 border rounded px-2 py-1 cursor-pointer ${form.target_type === t ? 'bg-primary/10 border-primary' : ''} ${targetLocked && t !== form.target_type ? 'opacity-40 pointer-events-none' : ''}`}>
                  <input type="radio" checked={form.target_type === t} onChange={() => setForm({ ...form, target_type: t, target_id: '', target_label: '' })} disabled={targetLocked && t !== form.target_type} data-testid={`adj-target-${t}`} />
                  {t === 'none' ? 'Ninguna' : t === 'supplier' ? 'Proveedor' : 'Residente'}
                </label>
              ))}
            </div>
            {form.target_type === 'supplier' && (
              <div>
                <select value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value, target_label: suppliers.find(s => s.supplier_id === e.target.value)?.business_name || '' })}
                  disabled={targetLocked}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-60"
                  data-testid="adj-supplier-select">
                  <option value="">{loadingList ? 'Cargando…' : 'Elegí un proveedor…'}</option>
                  {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.business_name} — {s.tax_id || 'S/CUIT'}</option>)}
                </select>
              </div>
            )}
            {form.target_type === 'resident' && (
              <div>
                <select value={form.target_id} onChange={(e) => { const r = residents.find(x => x.resident_id === e.target.value); setForm({ ...form, target_id: e.target.value, target_label: r ? `${r.last_name}, ${r.first_name}` : '' }); }}
                  disabled={targetLocked}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-60"
                  data-testid="adj-resident-select">
                  <option value="">{loadingList ? 'Cargando…' : 'Elegí un residente…'}</option>
                  {residents.map(r => <option key={r.resident_id} value={r.resident_id}>{r.last_name}, {r.first_name}</option>)}
                </select>
              </div>
            )}
            {form.target_type !== 'none' && (
              <div className="rounded border border-blue-200 bg-blue-50 p-2 text-[11px] text-blue-900 flex gap-2">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <div>
                  {form.adjustment_type === 'debit'
                    ? (form.target_type === 'supplier'
                        ? 'Débito de caja + proveedor: se registra un débito en la cta.cte. del proveedor (baja tu deuda hacia él).'
                        : 'Débito de caja + residente: se registra un crédito en la cta.cte. del residente (baja lo que te debe).')
                    : (form.target_type === 'supplier'
                        ? 'Crédito de caja + proveedor: se registra un crédito en la cta.cte. del proveedor (aumenta tu deuda hacia él).'
                        : 'Crédito de caja + residente: se registra un débito en la cta.cte. del residente (aumenta lo que te debe).')
                  }
                </div>
              </div>
            )}
          </div>

          <div>
            <Label>Monto *</Label>
            <MoneyInput value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="$ 0,00" data-testid="adj-amount" />
          </div>
          <div>
            <Label>Descripción *</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Motivo del ajuste..." data-testid="adj-desc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
          <Button onClick={onSave} data-testid="adj-save" disabled={submitting}>{submitting ? 'Registrando…' : 'Registrar Ajuste'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TransferDialog({ open, onOpenChange, form, setForm, onSave, onCancel, cashBanks, submitting = false }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-blue-500" />
            Nueva Transferencia Interna
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha *</Label>
              <Input type="date" max={today()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Monto *</Label>
              <MoneyInput value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="$ 0,00" />
            </div>
          </div>
          <div>
            <Label>Origen *</Label>
            <select value={form.origin_cash_bank_id} onChange={(e) => setForm({ ...form, origin_cash_bank_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Seleccionar caja/banco origen...</option>
              {cashBanks.map(cb => <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name} ({cb.currency})</option>)}
            </select>
          </div>
          <div>
            <Label>Destino *</Label>
            <select value={form.destination_cash_bank_id} onChange={(e) => setForm({ ...form, destination_cash_bank_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Seleccionar caja/banco destino...</option>
              {cashBanks.filter(cb => cb.cash_bank_id !== form.origin_cash_bank_id).map(cb => <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name} ({cb.currency})</option>)}
            </select>
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
          <Button onClick={onSave} disabled={submitting}>{submitting ? 'Registrando…' : 'Registrar Transferencia'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CheckDialog({ open, onOpenChange, form, setForm, onSave, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-orange-500" />
            Nuevo Valor en Cartera
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nº Cheque/Valor *</Label>
              <Input value={form.check_number} onChange={(e) => setForm({ ...form, check_number: e.target.value })} placeholder="Número" />
            </div>
            <div>
              <Label>Banco</Label>
              <Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} placeholder="Banco emisor" />
            </div>
          </div>
          <div>
            <Label>Emisor *</Label>
            <Input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="Nombre del emisor" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Monto *</Label>
              <MoneyInput value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="$ 0,00" />
            </div>
            <div>
              <Label>Moneda</Label>
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha Emisión *</Label>
              <Input type="date" max={today()} value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
            </div>
            <div>
              <Label>Fecha Vencimiento *</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onSave}>Registrar Valor</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DepositCheckDialog({ open, onOpenChange, check, cashBanks, onDeposit, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Depositar Cheque</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {check && (
            <div className="p-4 bg-muted rounded-lg">
              <p><strong>Cheque:</strong> {check.check_number}</p>
              <p><strong>Emisor:</strong> {check.issuer}</p>
              <p><strong>Monto:</strong> {fmtCurrency(check.amount, check.currency)}</p>
            </div>
          )}
          <div>
            <Label>Depositar en *</Label>
            <div className="space-y-2 mt-2">
              {cashBanks.map(cb => (
                <Button
                  key={cb.cash_bank_id}
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => onDeposit(cb.cash_bank_id)}
                >
                  {cb.name} ({cb.currency})
                </Button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RejectCheckDialog({ open, onOpenChange, check, reason, setReason, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-700">Rechazar Cheque</DialogTitle>
        </DialogHeader>
        {check && (
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
              <p><strong>Cheque:</strong> {check.check_number}</p>
              <p><strong>Emisor:</strong> {check.issuer}</p>
              <p><strong>Monto:</strong> {fmtCurrency(check.amount)}</p>
            </div>
            <div>
              <Label>Motivo del rechazo *</Label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] mt-1"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej: Sin fondos, firma no coincide, cheque adulterado..."
                data-testid="reject-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onCancel}>Cancelar</Button>
              <Button variant="destructive" onClick={onConfirm} data-testid="confirm-reject-btn">
                Confirmar Rechazo
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DeleteTreasuryDocDialog({ target, title, docLabel, amount, detail, reason, setReason, deleting, testId, confirmLabel, onConfirm, onClose }) {
  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid={`delete-${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Vas a {confirmLabel ? 'anular la' : 'eliminar la'} <b>{docLabel}</b> por <b>{fmtCurrency(amount || 0)}</b>. {detail}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`delete-${testId}-reason`}>Motivo * <span className="text-xs text-muted-foreground">(mín. 10 caracteres)</span></Label>
          <textarea
            id={`delete-${testId}-reason`}
            className="w-full min-h-[80px] rounded-md border px-3 py-2 text-sm"
            placeholder="Ej: Cargado por error. Se rehará con los datos correctos."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid={`delete-${testId}-reason`}
          />
          <p className="text-xs text-muted-foreground">{(reason || '').trim().length} / 10 caracteres mínimos</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancelar</Button>
          <Button variant="destructive" onClick={onConfirm}
            disabled={deleting || (reason || '').trim().length < 10}
            data-testid={`delete-${testId}-confirm`}>
            {deleting ? 'Procesando…' : (confirmLabel || 'Eliminar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteCollectionDialog({ target, reason, setReason, deleting, onConfirm, onClose }) {
  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="delete-collection-dialog">
        <DialogHeader>
          <DialogTitle>Eliminar Cobranza</DialogTitle>
          <DialogDescription>
            Vas a eliminar la <b>cobranza {target?.collection_number}</b> por <b>{target && fmtCurrency(target.total_amount)}</b>
            {target?.client_name ? <> de <b>{target.client_name}</b></> : null}.
            Se revertirán automáticamente: saldo de caja/banco, movimientos de tesorería, acumulado del concepto,
            la imputación a las facturas y el movimiento de cuenta corriente del residente (la deuda vuelve a quedar
            pendiente), el saldo a favor no usado y los cheques recibidos pendientes. En contabilidad se genera un
            <b> contra-asiento</b> con fecha de hoy: el asiento original queda visible marcado como reversado.
            La cobranza <b>sigue en el listado</b> marcada como eliminada, con tu usuario y el motivo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="delete-collection-reason">Motivo de eliminación * <span className="text-xs text-muted-foreground">(mín. 10 caracteres)</span></Label>
          <textarea
            id="delete-collection-reason"
            className="w-full min-h-[80px] rounded-md border px-3 py-2 text-sm"
            placeholder="Ej: Cobranza registrada por duplicado. Se rehará con el importe correcto."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="delete-collection-reason"
          />
          <p className="text-xs text-muted-foreground">{(reason || '').trim().length} / 10 caracteres mínimos</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancelar</Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={deleting || (reason || '').trim().length < 10}
            data-testid="delete-collection-confirm"
          >
            {deleting ? 'Eliminando…' : 'Eliminar cobranza'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeletePaymentOrderDialog({ target, reason, setReason, deleting, onConfirm, onClose }) {
  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="delete-op-dialog">
        <DialogHeader>
          <DialogTitle>Eliminar Orden de Pago</DialogTitle>
          <DialogDescription>
            Vas a eliminar <b>OP {target?.payment_order_number}</b> por <b>{target && fmtCurrency(target.total_amount)}</b>.
            Se revertirán automáticamente: saldo de caja/banco, movimientos de tesorería, cuenta corriente del proveedor (si aplica),
            acumulado del concepto y cheques diferidos pendientes (se eliminan). En contabilidad se genera un <b>contra-asiento</b> con
            fecha de hoy y el asiento original queda visible marcado como reversado.
            La OP queda en el sistema marcada como eliminada con tu usuario y el motivo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="delete-op-reason">Motivo de eliminación * <span className="text-xs text-muted-foreground">(mín. 10 caracteres)</span></Label>
          <textarea
            id="delete-op-reason"
            className="w-full min-h-[80px] rounded-md border px-3 py-2 text-sm"
            placeholder="Ej: Cargada por error en la fecha. Se rehará con fecha correcta."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="delete-op-reason"
          />
          <p className="text-xs text-muted-foreground">{(reason || '').trim().length} / 10 caracteres mínimos</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancelar</Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={deleting || (reason || '').trim().length < 10}
            data-testid="delete-op-confirm"
          >
            {deleting ? 'Eliminando…' : 'Eliminar OP'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
